import { useState } from 'react';

import { Account } from '@ndvr/portfolio-api';

import { NumberInput } from '@/components/NumberInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/utils/cn';
import { formatAccountType } from 'utils';
import { CashflowStream } from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/EditCashflowDialog';

export const ANY_ACCOUNT = 'portfolio_level';

export const primaryButton = 'h-9 rounded-lg bg-[#2C2424] px-4 text-[13.5px] font-medium text-white hover:bg-[#0D0D0D]';
const secondaryButton =
    'h-9 rounded-lg border-[#CECAC6] bg-white px-4 text-[13px] font-medium text-[#2C2424] hover:bg-[#F8F7F5]';

const frequencies: { value: CashflowStream['frequency']; label: string }[] = [
    { value: 'every-month', label: 'Monthly' },
    { value: 'every-3-months', label: 'Quarterly' },
    { value: 'every-6-months', label: 'Semi-annually' },
    { value: 'every-year', label: 'Annually' },
    { value: 'one-time', label: 'One-time' },
];

export const newWithdrawal = (): CashflowStream => ({
    id: '',
    name: '',
    type: 'withdrawal',
    frequency: 'every-3-months',
    amount: 0,
    startDate: new Date().toISOString().split('T')[0],
    adjustForInflation: false,
    inflationRate: 2.4,
    account: ANY_ACCOUNT,
    repeatUntil: 'cancel',
});

const invalid = (value: CashflowStream) =>
    !value.name.trim() ||
    !(value.amount > 0) ||
    !value.startDate ||
    (value.frequency !== 'one-time' && value.repeatUntil === 'specific-date' && !value.repeatEndDate);

const ChoiceCard = ({
    selected,
    className,
    children,
}: {
    selected: boolean;
    className?: string;
    children: React.ReactNode;
}) => (
    <div
        className={cn(
            'flex items-start gap-2.5 rounded-lg border bg-white px-3.5 py-3',
            selected ? 'border-[#2C2424] bg-[#FCFBFA]' : 'border-[#CECAC6]',
            className,
        )}
    >
        {children}
    </div>
);

interface PolicyWithdrawalFormProps {
    /** The withdrawal being edited, or a `newWithdrawal()` to add one. */
    value: CashflowStream;
    accounts: Account[];
    /** Called with the valid, trimmed withdrawal. */
    onSubmit: (value: CashflowStream) => void;
    onCancel: () => void;
}

/** Adds or edits one withdrawal. Shows its errors only after a submit attempt. */
export const PolicyWithdrawalForm = ({ value, accounts, onSubmit, onCancel }: PolicyWithdrawalFormProps) => {
    const [draft, setDraft] = useState(value);
    const [showErrors, setShowErrors] = useState(false);

    const patch = (changes: Partial<CashflowStream>) => setDraft((current) => ({ ...current, ...changes }));

    const submit = () => {
        if (invalid(draft)) {
            setShowErrors(true);
            return;
        }
        onSubmit({ ...draft, name: draft.name.trim() });
    };

    const isOneTime = draft.frequency === 'one-time';
    const nameMissing = showErrors && !draft.name.trim();
    const amountMissing = showErrors && !(draft.amount > 0);
    const specificAccount = draft.account !== ANY_ACCOUNT;

    return (
        <form
            className="flex min-h-0 flex-1 flex-col"
            noValidate
            onSubmit={(event) => {
                event.preventDefault();
                submit();
            }}
        >
            <div className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
                <div className="mx-auto max-w-[680px] space-y-[18px]">
                    <div className="space-y-[7px]">
                        <Label htmlFor="withdrawal-name" className="text-[12px] font-medium text-[#5A4F4E]">
                            Name
                        </Label>
                        <Input
                            id="withdrawal-name"
                            value={draft.name}
                            maxLength={64}
                            aria-invalid={nameMissing}
                            className="h-[38px] rounded-lg border-[#CECAC6] bg-white px-3"
                            placeholder="e.g. Annual living draw"
                            onChange={(event) => patch({ name: event.target.value })}
                        />
                        {nameMissing && <p className="text-destructive text-sm">Name is required</p>}
                    </div>
                    <div className="space-y-[7px]">
                        <Label id="withdrawal-frequency-label" className="text-[12px] font-medium text-[#5A4F4E]">
                            Frequency
                        </Label>
                        <div
                            role="radiogroup"
                            aria-labelledby="withdrawal-frequency-label"
                            className="flex flex-wrap gap-2"
                        >
                            {frequencies.map((item) => (
                                <Button
                                    key={item.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={draft.frequency === item.value}
                                    size="sm"
                                    variant={draft.frequency === item.value ? 'default' : 'outline'}
                                    className={cn(
                                        'h-8 rounded-lg px-3 text-[12.5px] font-medium',
                                        draft.frequency === item.value
                                            ? 'bg-[#2C2424] text-white hover:bg-[#0D0D0D]'
                                            : 'border-[#CECAC6] bg-white text-[#2C2424]',
                                    )}
                                    onClick={() => patch({ frequency: item.value })}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-[7px]">
                        <Label htmlFor="withdrawal-amount" className="text-[12px] font-medium text-[#5A4F4E]">
                            Amount
                        </Label>
                        <div className="relative">
                            <span
                                aria-hidden="true"
                                className="text-cocoa-500 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm"
                            >
                                $
                            </span>
                            <NumberInput
                                id="withdrawal-amount"
                                className="h-[38px] rounded-lg border-[#CECAC6] bg-white pl-7"
                                value={draft.amount || ''}
                                aria-invalid={amountMissing}
                                onChange={(event) => patch({ amount: Number(event.target.value) })}
                            />
                        </div>
                        {amountMissing && <p className="text-destructive text-sm">Amount must be positive</p>}
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                        <div className="space-y-[7px]">
                            <Label htmlFor="withdrawal-start" className="text-[12px] font-medium text-[#5A4F4E]">
                                Starts
                            </Label>
                            <Input
                                id="withdrawal-start"
                                className="h-[38px] rounded-lg border-[#CECAC6] bg-white px-3"
                                type="date"
                                value={draft.startDate}
                                onChange={(event) => patch({ startDate: event.target.value })}
                            />
                        </div>
                        {!isOneTime && (
                            <div className="space-y-[7px]">
                                <Label htmlFor="withdrawal-end" className="text-[12px] font-medium text-[#5A4F4E]">
                                    Ends <span className="text-cocoa-500 font-normal">· optional</span>
                                </Label>
                                <Input
                                    id="withdrawal-end"
                                    className="h-[38px] rounded-lg border-[#CECAC6] bg-white px-3"
                                    type="date"
                                    value={draft.repeatUntil === 'specific-date' ? (draft.repeatEndDate ?? '') : ''}
                                    onChange={(event) =>
                                        patch(
                                            event.target.value
                                                ? { repeatUntil: 'specific-date', repeatEndDate: event.target.value }
                                                : { repeatUntil: 'cancel', repeatEndDate: undefined },
                                        )
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <div className="space-y-[7px]">
                        <Label id="withdrawal-funding-label" className="text-[12px] font-medium text-[#5A4F4E]">
                            Funded from
                        </Label>
                        <RadioGroup
                            aria-labelledby="withdrawal-funding-label"
                            value={specificAccount ? 'specific' : 'engine'}
                            onValueChange={(choice) =>
                                patch({
                                    account:
                                        choice === 'engine' ? ANY_ACCOUNT : (accounts[0]?.accountGUID ?? ANY_ACCOUNT),
                                })
                            }
                            className="grid-cols-1 gap-3 sm:grid-cols-2"
                        >
                            <Label htmlFor="withdrawal-funding-engine" className="block cursor-pointer">
                                <ChoiceCard selected={!specificAccount} className="h-full">
                                    <RadioGroupItem id="withdrawal-funding-engine" value="engine" />
                                    <span className="space-y-0.5">
                                        <span className="block text-[13.5px] font-medium">
                                            Let the Portfolio Engine decide
                                        </span>
                                        <span className="block text-xs leading-[1.4] font-normal text-[#897E7B]">
                                            Draws come from the most tax-efficient account each time.
                                        </span>
                                    </span>
                                </ChoiceCard>
                            </Label>
                            <Label
                                htmlFor="withdrawal-funding-specific"
                                className={cn('block cursor-pointer', accounts.length === 0 && 'opacity-50')}
                            >
                                <ChoiceCard selected={specificAccount} className="h-full">
                                    <RadioGroupItem
                                        id="withdrawal-funding-specific"
                                        value="specific"
                                        disabled={accounts.length === 0}
                                    />
                                    <span className="space-y-0.5">
                                        <span className="block text-[13.5px] font-medium">
                                            Withdraw from a specific account
                                        </span>
                                        <span className="block text-xs leading-[1.4] font-normal text-[#897E7B]">
                                            Pick one account in this portfolio.
                                        </span>
                                    </span>
                                </ChoiceCard>
                            </Label>
                        </RadioGroup>
                        {specificAccount && (
                            <RadioGroup
                                aria-label="Account to withdraw from"
                                value={draft.account}
                                onValueChange={(account) => patch({ account })}
                                className="mt-3 gap-0 overflow-hidden rounded-lg border border-stone-200"
                            >
                                <div className="text-cocoa-500 grid grid-cols-[1fr_auto] gap-4 border-b border-stone-200 bg-stone-50 px-4 py-2 text-xs font-medium tracking-wide uppercase">
                                    <span className="h-[38px] rounded-lg border-[#CECAC6] bg-white pl-7">Account</span>
                                    <span className="w-28">Type</span>
                                </div>
                                {accounts.map((item) => (
                                    <Label
                                        key={item.accountGUID}
                                        htmlFor={`withdrawal-account-${item.accountGUID}`}
                                        className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-4 border-b border-stone-100 px-4 py-2.5 font-normal last:border-b-0"
                                    >
                                        <span className="flex items-center gap-3 text-sm">
                                            <RadioGroupItem
                                                id={`withdrawal-account-${item.accountGUID}`}
                                                value={item.accountGUID}
                                            />
                                            {item.accountName.plainText}
                                        </span>
                                        <span className="text-cocoa-500 w-28 text-xs">
                                            {formatAccountType(item.accountType)}
                                        </span>
                                    </Label>
                                ))}
                            </RadioGroup>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[#CECAC6] bg-[#FCFBFA] px-6 py-3.5">
                <Button type="button" variant="outline" className={secondaryButton} onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" className={primaryButton}>
                    {draft.id ? 'Update withdrawal' : 'Add withdrawal'}
                </Button>
            </div>
        </form>
    );
};
